if (!customElements.get('product-form')) {
  customElements.define(
    'product-form',
    class ProductForm extends HTMLElement {
      constructor() {
        super();

        this.form = this.querySelector('form');
        this.variantIdInput.disabled = false;

        this.onSubmitHandler = this.onSubmitHandler.bind(this);
        this.form.addEventListener('submit', this.onSubmitHandler);

        this.cart =
          document.querySelector('cart-notification') ||
          document.querySelector('cart-drawer');

        this.submitButton = this.querySelector('[type="submit"]');
        this.submitButtonText = this.submitButton.querySelector('span');

        if (document.querySelector('cart-drawer')) {
          this.submitButton.setAttribute('aria-haspopup', 'dialog');
        }

        this.hideErrors = this.dataset.hideErrors === 'true';
      }

      // -----------------------------
      // Helpers
      // -----------------------------

      async getCart() {
        const res = await fetch(`${window.routes.cart_url}.js`, {
          credentials: 'same-origin',
          headers: { Accept: 'application/json' },
        });
        if (!res.ok) throw new Error('Failed to load cart');
        return res.json();
      }

      getVariantQtyInCart(cart, variantId) {
        const id = Number(variantId);
        const item = (cart.items || []).find(
          (i) => Number(i.variant_id) === id
        );
        return item ? Number(item.quantity) : 0;
      }

      getMaxInventory() {
        const raw = this.dataset.maxInventory;
        const max = Number(raw);
        return Number.isFinite(max) ? max : 999999;
      }

async refreshCartSections() {
  if (!this.cart) return;

  try {
    // Some themes return objects, some strings, normalise to id list
    const sectionIds = (this.cart.getSectionsToRender?.() || [])
      .map((s) => (typeof s === 'string' ? s : s.id))
      .filter(Boolean);

    // If we can't get sections for any reason, just bail (nothing to render)
    if (!sectionIds.length) return;

    const sectionsParam = sectionIds.join(',');

    const res = await fetch(`${window.routes.cart_url}?sections=${sectionsParam}`, {
      credentials: 'same-origin',
      headers: { Accept: 'application/json' },
    });

    const sectionsResponse = await res.json();

    // Dawn renderContents expects an object shaped like { sections: {...} }
    if (typeof this.cart.renderContents === 'function') {
      this.cart.renderContents({ sections: sectionsResponse });
    }

    // KEY FIX FOR MOBILE:
    // cart drawer often needs opening explicitly after we re-render
    if (this.cart.tagName?.toLowerCase() === 'cart-drawer' && typeof this.cart.open === 'function') {
      this.cart.open();
    }

    // If it's cart-notification, some themes have open as well
    if (this.cart.tagName?.toLowerCase() === 'cart-notification' && typeof this.cart.open === 'function') {
      this.cart.open();
    }
  } catch (e) {
    console.error('Cart refresh failed', e);
  }
}

      // -----------------------------
      // Submit
      // -----------------------------

      async onSubmitHandler(evt) {
        evt.preventDefault();
        if (this.submitButton.getAttribute('aria-disabled') === 'true') return;

        this.handleErrorMessage();

        this.submitButton.setAttribute('aria-disabled', true);
        this.submitButton.classList.add('loading');

        const config = fetchConfig('javascript');
        config.headers['X-Requested-With'] = 'XMLHttpRequest';
        delete config.headers['Content-Type'];

        const formData = new FormData(this.form);

        // -----------------------------
        // STOCK CLAMP LOGIC
        // -----------------------------

        const variantId = formData.get('id');
        const requestedQty = Number(formData.get('quantity') || 1);
        const maxInventory = this.getMaxInventory();

        let clampedQty = requestedQty;
        let wasClamped = false;

        if (Number.isFinite(maxInventory) && maxInventory !== 999999) {
          try {
            const cart = await this.getCart();
            const inCartQty = this.getVariantQtyInCart(cart, variantId);
            const allowedToAdd = Math.max(
              0,
              maxInventory - inCartQty
            );

            clampedQty = Math.min(requestedQty, allowedToAdd);
            wasClamped = clampedQty < requestedQty;

            // already maxed in cart
            if (clampedQty <= 0) {
              this.handleErrorMessage(
                'No more stock available for this item.'
              );
              this.submitButton.classList.remove('loading');
              this.submitButton.removeAttribute('aria-disabled');
              this.error = true;
              return;
            }

            formData.set('quantity', String(clampedQty));
          } catch (e) {
            // fallback clamp
            clampedQty = Math.min(requestedQty, maxInventory);
            wasClamped = clampedQty < requestedQty;
            formData.set('quantity', String(clampedQty));
          }
        }

        // -----------------------------
        // Section rendering setup
        // -----------------------------

        if (this.cart) {
          formData.append(
            'sections',
            this.cart.getSectionsToRender().map((section) => section.id)
          );
          formData.append('sections_url', window.location.pathname);
          this.cart.setActiveElement(document.activeElement);
        }

        config.body = formData;

        // -----------------------------
        // ADD TO CART
        // -----------------------------

        fetch(`${routes.cart_add_url}`, config)
          .then((response) => response.json())
          .then(async (response) => {
            // 🔴 IMPORTANT FIX
            // Shopify can return status=true BUT still add items
            if (response.status) {
              publish(PUB_SUB_EVENTS.cartError, {
                source: 'product-form',
                productVariantId: formData.get('id'),
                errors: response.errors || response.description,
                message: response.message,
              });

              this.handleErrorMessage(response.description);

              // ✅ FORCE CART SYNC (this fixes your issue)
              await this.refreshCartSections();

              const soldOutMessage =
                this.submitButton.querySelector('.sold-out-message');

              if (soldOutMessage) {
                this.submitButton.setAttribute('aria-disabled', true);
                this.submitButtonText.classList.add('hidden');
                soldOutMessage.classList.remove('hidden');
              }

              this.error = true;
              return;
            }

            if (!this.cart) {
              window.location = window.routes.cart_url;
              return;
            }

            const startMarker =
              CartPerformance.createStartingMarker(
                'add:wait-for-subscribers'
              );

            if (!this.error)
              await publish(PUB_SUB_EVENTS.cartUpdate, {
                source: 'product-form',
                productVariantId: formData.get('id'),
                cartData: response,
              }).then(() => {
                CartPerformance.measureFromMarker(
                  'add:wait-for-subscribers',
                  startMarker
                );
              });

            this.error = false;

            // ✅ show clamp message AFTER success
            if (wasClamped) {
              const itemWord =
                clampedQty === 1 ? 'item was' : 'items were';
              this.handleErrorMessage(
                `Only ${clampedQty} ${itemWord} added to your cart due to availability.`
              );
            }

            const quickAddModal = this.closest('quick-add-modal');

            if (quickAddModal) {
              document.body.addEventListener(
                'modalClosed',
                () => {
                  setTimeout(() => {
                    CartPerformance.measure(
                      'add:paint-updated-sections',
                      () => {
                        this.cart.renderContents(response);
                      }
                    );
                  });
                },
                { once: true }
              );
              quickAddModal.hide(true);
            } else {
              CartPerformance.measure(
                'add:paint-updated-sections',
                () => {
                  this.cart.renderContents(response);
                }
              );
            }
          })
          .catch((e) => {
            console.error(e);
          })
          .finally(() => {
            this.submitButton.classList.remove('loading');

            if (this.cart && this.cart.classList.contains('is-empty')) {
              this.cart.classList.remove('is-empty');
            }

            if (!this.error) {
              this.submitButton.removeAttribute('aria-disabled');
            }

            CartPerformance.measureFromEvent(
              'add:user-action',
              evt
            );
          });
      }

      // -----------------------------
      // Error UI
      // -----------------------------

      handleErrorMessage(errorMessage = false) {
        if (this.hideErrors) return;

        this.errorMessageWrapper =
          this.errorMessageWrapper ||
          this.querySelector('.product-form__error-message-wrapper');

        if (!this.errorMessageWrapper) return;

        this.errorMessage =
          this.errorMessage ||
          this.errorMessageWrapper.querySelector(
            '.product-form__error-message'
          );

        this.errorMessageWrapper.toggleAttribute(
          'hidden',
          !errorMessage
        );

        if (errorMessage) {
          this.errorMessage.textContent = errorMessage;
        }
      }

      toggleSubmitButton(disable = true, text) {
        if (disable) {
          this.submitButton.setAttribute('disabled', 'disabled');
          if (text) this.submitButtonText.textContent = text;
        } else {
          this.submitButton.removeAttribute('disabled');
          this.submitButtonText.textContent =
            window.variantStrings.addToCart;
        }
      }

      get variantIdInput() {
        return this.form.querySelector('[name=id]');
      }
    }
  );
}